<template>
  <v-sheet :rounded="isSmallerDisplay() ? '' : 'lg'">
    <div v-if="isAppAdmin(this.$store.state.user._id)">
      <!-- Header -->
      <v-card class="mb-3">
        <v-card-title>
          Academy Groups
          <v-spacer></v-spacer>
          <v-btn small color="primary" outlined @click="createDialog = true">
            <v-icon left size="16">mdi-plus</v-icon>New Group
          </v-btn>
        </v-card-title>
      </v-card>

      <v-skeleton-loader v-if="loading" type="card, card, card"></v-skeleton-loader>

      <div v-else-if="!groups.length" class="text-center pa-8 grey--text">
        No groups yet. Create one above.
      </div>

      <v-expansion-panels v-else multiple accordion>
        <v-expansion-panel v-for="group in groups" :key="group._id">
          <v-expansion-panel-header>
            <div class="d-flex align-center">
              <img v-if="group.groupLogo" :src="group.groupLogo"
                style="max-height:24px;max-width:60px;object-fit:contain;margin-right:8px;" />
              <v-chip x-small :color="group.active ? 'success' : 'grey'" dark class="mr-2">{{ group.active ? 'Active' :
                'Inactive' }}</v-chip>
              <span class="font-weight-medium">{{ group.name }}</span>
              <span class="ml-2 caption grey--text">({{ group.academies.length }} academies, {{
                group.groupOwners.length }} managers)</span>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <!-- Group actions row -->
            <div class="d-flex mb-2 flex-wrap align-center" style="gap:8px;">
              <v-btn x-small outlined @click="startEditName(group)">
                <v-icon left size="14">mdi-pencil</v-icon>Rename
              </v-btn>
              <image-upload-croppie :success-function="logoUploaderFor(group)" :vm="null"></image-upload-croppie>
              <v-btn x-small outlined color="error" @click="confirmDelete(group)">
                <v-icon left size="14">mdi-delete</v-icon>Delete Group
              </v-btn>
            </div>
            <div v-if="group.groupLogo" class="d-flex align-center mb-3" style="gap:8px;">
              <img :src="group.groupLogo"
                style="max-height:40px;max-width:100px;object-fit:contain;border:1px solid rgba(128,128,128,0.2);border-radius:4px;padding:4px;" />
              <span class="caption grey--text">Current logo</span>
            </div>

            <!-- Academies list -->
            <v-list dense class="mb-2">
              <v-list-item v-for="academy in group.academies" :key="academy._id" class="px-0">
                <v-list-item-content>
                  <v-list-item-title class="body-2">
                    <a :href="'https://connect.bjjlink.com/secure/academy/' + academy._id" target="_blank"
                      rel="noopener">{{ academy.name }}</a><span v-if="academy.affiliation" class="grey--text"> ({{
                        academy.affiliation }})</span>
                  </v-list-item-title>
                  <v-list-item-subtitle class="caption">
                    {{ academy.country }}<span v-if="academy.cityDisplay"> · {{ academy.cityDisplay }}</span>
                    <v-chip v-if="academy.managedAcademyId" x-small color="blue lighten-4" class="ml-1">Managed</v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action class="d-flex flex-row align-center my-0" style="gap:4px;">
                  <!-- Manager toggle -->
                  <v-chip v-if="isManager(group, academy)" x-small color="primary" dark close
                    @click:close="removeManager(group, academy)">Manager</v-chip>
                  <v-btn v-else-if="academy.managedAcademyId" x-small outlined color="primary"
                    @click="addManager(group, academy)">Make Admin</v-btn>

                  <!-- Remove from group -->
                  <v-btn x-small icon color="error" @click="removeAcademy(group, academy)">
                    <v-icon size="16">mdi-close</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>

              <v-list-item v-if="!group.academies.length" class="px-0">
                <v-list-item-content>
                  <v-list-item-subtitle class="caption">No academies in this group yet.</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <!-- Add academy autocomplete (multi-select) -->
            <v-autocomplete v-model="selectedAcademies[group._id]" :items="searchResults"
              :search-input.sync="searchQuery" item-text="name" item-value="_id" return-object multiple chips
              small-chips deletable-chips dense outlined label="Search academies to add…" :loading="searching"
              no-filter clearable hide-details>
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-on="on" v-bind="attrs">
                  <v-list-item-action>
                    <v-checkbox :input-value="attrs.inputValue" color="primary" dense hide-details></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}<span v-if="item.affiliation" class="grey--text"> ({{
                      item.affiliation }})</span></v-list-item-title>
                    <v-list-item-subtitle class="caption">
                      {{ item.country }}<span v-if="item.cityDisplay"> · {{ item.cityDisplay }}</span>
                      <v-chip v-if="item.managedAcademyId" x-small color="primary" dark class="ml-1">Managed</v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-title class="caption grey--text">
                    {{ searchQuery && searchQuery.length >= 2 ? 'No results' : 'Type 2+ characters to search' }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-autocomplete>
            <v-btn x-small outlined color="primary" class="mt-2"
              :disabled="!selectedAcademies[group._id] || !selectedAcademies[group._id].length"
              @click="addAcademies(group)">
              <v-icon left size="14">mdi-plus</v-icon>Add Selected
            </v-btn>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Create Group Dialog -->
      <v-dialog v-model="createDialog" max-width="400">
        <v-card>
          <v-card-title class="text-h6">New Group</v-card-title>
          <v-card-text>
            <v-text-field outlined dense v-model="newGroupName" label="Group Name" autofocus
              @keyup.enter="createGroup"></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="createDialog = false">Cancel</v-btn>
            <v-btn color="primary" :disabled="!newGroupName || saving" @click="createGroup">Create</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Rename Dialog -->
      <v-dialog v-model="editNameDialog" max-width="400">
        <v-card>
          <v-card-title class="text-h6">Rename Group</v-card-title>
          <v-card-text>
            <v-text-field outlined dense v-model="editingName" label="Group Name" autofocus
              @keyup.enter="saveName"></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="editNameDialog = false">Cancel</v-btn>
            <v-btn color="primary" :disabled="!editingName || saving" @click="saveName">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirm Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
          <v-card-title class="text-h6 error--text">Delete Group</v-card-title>
          <v-card-text>
            Delete "<strong>{{ deletingGroup && deletingGroup.name }}</strong>"? This will deactivate the group.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDialog = false">Cancel</v-btn>
            <v-btn color="error" :disabled="saving" @click="deleteGroup">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top>
        {{ snackbarText }}
      </v-snackbar>
    </div>
  </v-sheet>
</template>

<script>
import BjjlinkAdminService from "@/services/BjjlinkAdminService";
import ImageUploadCroppie from "@/components/inputs/ImageUploadCroppie.vue";

export default {
  name: "ManagedAcademyGroups",
  components: { ImageUploadCroppie },
  data() {
    return {
      loading: false,
      saving: false,
      groups: [],
      // Create
      createDialog: false,
      newGroupName: "",
      // Rename
      editNameDialog: false,
      editingGroup: null,
      editingName: "",
      // Delete
      deleteDialog: false,
      deletingGroup: null,
      // Search
      searchQuery: "",
      searchResults: [],
      searching: false,
      selectedAcademies: {},
      searchDebounce: null,
      // Snackbar
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
    };
  },
  mounted() {
    this.loadGroups();
  },
  watch: {
    searchQuery(val) {
      clearTimeout(this.searchDebounce);
      if (!val || val.length < 2) {
        this.searchResults = [];
        return;
      }
      this.searchDebounce = setTimeout(() => this.searchAcademies(val), 400);
    },
  },
  methods: {
    async loadGroups() {
      this.loading = true;
      try {
        const data = await BjjlinkAdminService.getAcademyGroups();
        this.groups = data.groups;
        const sel = {};
        data.groups.forEach((g) => { sel[g._id] = []; });
        this.selectedAcademies = sel;
        this.syncSelectedWithGroupMembership();
      } catch (e) {
        this.showError("Failed to load groups");
      }
      this.loading = false;
    },

    async searchAcademies(q) {
      this.searching = true;
      try {
        const data = await BjjlinkAdminService.searchAcademiesForGroup(q);
        this.searchResults = data.academies;
        this.syncSelectedWithGroupMembership();
      } catch (e) {
        this.searchResults = [];
      }
      this.searching = false;
    },

    // Pre-check (in each group's autocomplete) any search result that's already a member of that group.
    syncSelectedWithGroupMembership() {
      this.groups.forEach((group) => {
        const existingIds = new Set(group.academies.map((a) => String(a._id)));
        const alreadyInGroup = this.searchResults.filter((a) => existingIds.has(String(a._id)));
        const current = this.selectedAcademies[group._id] || [];
        const currentIds = new Set(current.map((a) => String(a._id)));
        const merged = current.concat(
          alreadyInGroup.filter((a) => !currentIds.has(String(a._id))),
        );
        this.$set(this.selectedAcademies, group._id, merged);
      });
    },

    isManager(group, academy) {
      if (!academy.managedAcademyId) return false;
      return group.groupOwners.some((o) => {
        const id = typeof o === "object" ? String(o._id) : String(o);
        return id === academy.managedAcademyId;
      });
    },

    async addAcademies(group) {
      const selected = this.selectedAcademies[group._id] || [];
      if (!selected.length) return;
      this.$set(this.selectedAcademies, group._id, []);
      this.searchQuery = "";
      const existingIds = new Set(group.academies.map((a) => String(a._id)));
      const newIds = selected
        .map((a) => a._id)
        .filter((id) => !existingIds.has(String(id)));
      if (!newIds.length) {
        this.showError("Academy(ies) already in this group");
        return;
      }
      const academyIds = group.academies.map((a) => a._id).concat(newIds);
      await this.patchGroup(group._id, { academies: academyIds });
    },

    async removeAcademy(group, academy) {
      const academyIds = group.academies
        .filter((a) => String(a._id) !== String(academy._id))
        .map((a) => a._id);
      const ownerIds = group.groupOwners
        .filter((o) => {
          const id = typeof o === "object" ? String(o._id) : String(o);
          return id !== academy.managedAcademyId;
        })
        .map((o) => (typeof o === "object" ? o._id : o));
      await this.patchGroup(group._id, { academies: academyIds, groupOwners: ownerIds });
    },

    async addManager(group, academy) {
      const ownerIds = group.groupOwners.map((o) =>
        typeof o === "object" ? String(o._id) : String(o),
      );
      if (!ownerIds.includes(academy.managedAcademyId)) {
        ownerIds.push(academy.managedAcademyId);
      }
      await this.patchGroup(group._id, { groupOwners: ownerIds });
    },

    async removeManager(group, academy) {
      const ownerIds = group.groupOwners
        .filter((o) => {
          const id = typeof o === "object" ? String(o._id) : String(o);
          return id !== academy.managedAcademyId;
        })
        .map((o) => (typeof o === "object" ? o._id : o));
      await this.patchGroup(group._id, { groupOwners: ownerIds });
    },

    async patchGroup(groupId, data) {
      this.saving = true;
      try {
        await BjjlinkAdminService.updateAcademyGroup(groupId, data);
        await this.loadGroups();
        this.showSuccess("Saved");
      } catch (e) {
        this.showError("Failed to save");
      }
      this.saving = false;
    },

    async createGroup() {
      if (!this.newGroupName) return;
      this.saving = true;
      try {
        await BjjlinkAdminService.createAcademyGroup({ name: this.newGroupName });
        this.newGroupName = "";
        this.createDialog = false;
        await this.loadGroups();
        this.showSuccess("Group created");
      } catch (e) {
        this.showError("Failed to create group");
      }
      this.saving = false;
    },

    startEditName(group) {
      this.editingGroup = group;
      this.editingName = group.name;
      this.editNameDialog = true;
    },

    async saveName() {
      if (!this.editingName) return;
      this.saving = true;
      try {
        await BjjlinkAdminService.updateAcademyGroup(this.editingGroup._id, {
          name: this.editingName,
        });
        this.editNameDialog = false;
        await this.loadGroups();
        this.showSuccess("Renamed");
      } catch (e) {
        this.showError("Failed to rename");
      }
      this.saving = false;
    },

    confirmDelete(group) {
      this.deletingGroup = group;
      this.deleteDialog = true;
    },

    async deleteGroup() {
      this.saving = true;
      try {
        await BjjlinkAdminService.deleteAcademyGroup(this.deletingGroup._id);
        this.deleteDialog = false;
        this.deletingGroup = null;
        await this.loadGroups();
        this.showSuccess("Group deleted");
      } catch (e) {
        this.showError("Failed to delete");
      }
      this.saving = false;
    },

    logoUploaderFor(group) {
      return (data) => {
        this.patchGroup(group._id, { groupLogo: data.location });
      };
    },

    showSuccess(text) {
      this.snackbarText = text;
      this.snackbarColor = "success";
      this.snackbar = true;
    },

    showError(text) {
      this.snackbarText = text;
      this.snackbarColor = "error";
      this.snackbar = true;
    },
  },
};
</script>
